/**
 * ADR-0115 — L'ERRORE DI ATTERRAGGIO E' UN MESSAGGIO SOPRA UNA SUPERFICIE VIVA.
 *
 * Questo file e' la META' PURA della cosa: da un esito di atterraggio ricava
 * COSA dire e QUALE azione offrire. Non renderizza, non naviga, non legge
 * `import.meta.env`, non importa React — cosi' `tools/verify-landing.ts` puo'
 * esercitarlo con il solo Node, come gli altri cancelli.
 *
 * ═══ 🔴 LA PARTE CHE NON E' ARREDAMENTO: L'AZIONE STA DENTRO ═══
 * L'ADR lo motiva, e la motivazione e' operativa, non estetica: **una modale che
 * compare all'avvio puo' essere chiusa senza essere letta**, e allora
 * l'informazione e' persa per sempre. Quindi il bottone PRIMARIO della modale
 * non e' «chiudi»: e' la cosa che serve a QUELL'esito — cosi' un tocco distratto
 * sul tasto piu' evidente fa comunque la cosa giusta.
 * ⛔ Da cui il divieto che questo file esiste per rendere verificabile: **niente
 * «vai alla Hub» ripetuto nove volte.** Sarebbe la schermata di prima con un
 * vestito nuovo — e chiudendo la modale sulla Hub ci si e' GIA'.
 * ⇒ Un esito senza azione utile porta `action: null` DICHIARATO, e mostra solo
 *   «chiudi». Dichiarare l'assenza e' diverso dal riempirla con un ripiego.
 *
 * ═══ LE QUATTRO CAUSE DELL'ADR SONO UN SOTTOINSIEME DEI NOVE ESITI ═══
 * §Decisione 3 nomina quattro cause. La schermata invito costruita da `tma5` ne
 * distingue NOVE, e le altre cinque non sono rumore: sono i rifiuti normali di un
 * bearer monouso (`InviteLanding.tsx` §«I RIFIUTI NON SONO CASI LIMITE»).
 * ⇒ Il campo `cause` marca quale dei nove E' una delle quattro dell'ADR e quale
 *   no. Serve a rendere leggibile la copertura senza appiattire nove esiti su
 *   quattro — appiattirli butterebbe via l'unica informazione che abbiamo.
 * ⚠️ NON si conia qui una tassonomia generale dei tipi di link: l'estensione a
 *   ogni tipo (§Decisione 1) e la superficie piu' vicina (§Decisione 2) sono
 *   derivazioni di or52 ancora aperte. Questo file copre il CASO INVITO, che e'
 *   la parte decisa da Luca.
 */

/**
 * Le quattro cause di §Decisione 3, testuali.
 * `null` = esito reale che non e' una di quelle quattro.
 */
export type AdrCause =
  /** «il tipo non esiste in questa build» */
  | 'unsupported'
  /** «la risorsa e' scaduta» */
  | 'expired'
  /** «c'e' ma non e' visibile a te» */
  | 'forbidden'
  /** «non esiste piu'» */
  | 'gone';

/**
 * L'azione DENTRO la modale.
 *
 * `navigate` porta su una superficie dove l'esito si puo' effettivamente
 * ribaltare; `retry` rilegge la stessa risorsa. Non esiste un terzo tipo
 * «chiudi»: la chiusura c'e' sempre e non e' un'azione, e' l'uscita.
 */
export type LandingAction =
  | { kind: 'navigate'; to: string; labelKey: string }
  | { kind: 'retry'; labelKey: string };

export interface LandingNotice {
  /** Chiavi nel namespace i18n `landing`, generico per costruzione. */
  titleKey: string;
  bodyKey: string;
  /** `null` DICHIARATO: per questo esito non esiste un passo utile. */
  action: LandingAction | null;
  cause: AdrCause | null;
}

/**
 * I nove esiti di atterraggio dell'invito, nell'ordine di precedenza in cui
 * `InviteLanding` li decide. L'ordine conta: un codice malformato non e'
 * «scaduto», e una build senza la sezione non e' «invito non valido».
 */
export type InviteOutcome =
  | 'malformed'
  | 'unavailable'
  | 'beta'
  | 'expired'
  | 'gone'
  | 'own'
  | 'capYou'
  | 'capCreator'
  | 'unreachable';

export const INVITE_OUTCOMES: readonly InviteOutcome[] = [
  'malformed',
  'unavailable',
  'beta',
  'expired',
  'gone',
  'own',
  'capYou',
  'capCreator',
  'unreachable',
] as const;

/** La lobby: l'unica superficie da cui si crea un invito, si incolla un codice
 *  a mano e si vedono le proprie partite. Esiste solo col flag acceso E il ruolo
 *  beta — v. `canReachBackgammon`. */
const BACKGAMMON = '/backgammon';

export interface InviteNoticeContext {
  /**
   * 🔴 Se la lobby e' RAGGIUNGIBILE: flag acceso E ruolo beta.
   *
   * Senza questo controllo l'azione di `malformed` punterebbe a una rotta che su
   * una build di produzione NON ESISTE (`featureBackgammonEnabled=false`), e il
   * tocco finirebbe sul catch-all `router.tsx:513` → redirect muto alla Hub.
   * Cioe' il fallimento silenzioso, rimesso dentro dalla porta dell'azione che
   * doveva toglierlo. `malformed` e' l'unico esito che si decide PRIMA di
   * guardare flag e ruolo, quindi e' l'unico che puo' inciamparci.
   */
  canReachBackgammon: boolean;
}

/**
 * Da un esito dell'invito alla modale che lo racconta.
 *
 * Funzione totale su `InviteOutcome`: ogni esito ha una voce esplicita, cosi'
 * aggiungerne uno rompe il `switch` invece di cadere in un ramo generico che
 * direbbe la cosa sbagliata in silenzio.
 */
export function inviteNotice(outcome: InviteOutcome, ctx: InviteNoticeContext): LandingNotice {
  const key = (suffix: string): string => `invite.${outcome}.${suffix}`;
  const toLobby = (labelKey: string): LandingAction | null =>
    ctx.canReachBackgammon ? { kind: 'navigate', to: BACKGAMMON, labelKey } : null;

  switch (outcome) {
    // Il link e' rotto, ma il CODICE si puo' ancora battere a mano dalla lobby.
    // Unico esito deciso prima di flag e ruolo: l'azione puo' non esserci.
    case 'malformed':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: toLobby('action.enterCode'),
        cause: null,
      };

    // §Decisione 3 «il tipo non esiste in questa build». La copy lo dice gia':
    // «riprovare non cambia nulla». Un'azione qui sarebbe una bugia gentile.
    case 'unavailable':
      return { titleKey: key('title'), bodyKey: key('body'), action: null, cause: 'unsupported' };

    // §Decisione 3 «c'e' ma non e' visibile a te». Il ruolo beta lo concede una
    // persona, non un bottone: niente azione, e la copy dice a chi chiedere.
    case 'beta':
      return { titleKey: key('title'), bodyKey: key('body'), action: null, cause: 'forbidden' };

    // §Decisione 3 «la risorsa e' scaduta». Non si puo' CHIEDERE un invito
    // dall'app — ma si puo' mandarne uno, che e' il passo che sblocca davvero.
    // ⚠️ `expired` e' anche «mai vista viva»: scaduta, presa o annullata prima
    // che arrivassimo (il 404 non dice quale), e la copy nomina tutte e tre.
    // La `cause` resta `expired` perche' l'esito copre anche la scadenza certa
    // (`refusalFor404`) e perche' `verify-landing` §4 vuole un esito per
    // ciascuna delle quattro cause: e' un'etichetta di copertura, non
    // un'affermazione — sul 404 dell'anteprima la causa puo' essere anche
    // «non esiste piu'».
    case 'expired':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: toLobby('action.inviteBack'),
        cause: 'expired',
      };

    // §Decisione 3 «non esiste piu'»: preso da un altro, o annullato.
    case 'gone':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: toLobby('action.inviteBack'),
        cause: 'gone',
      };

    // L'ha creato lui: il passo utile e' tornare dove lo si ri-condivide.
    case 'own':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: toLobby('action.openLobby'),
        cause: null,
      };

    // Sei pieno tu: la lobby e' dove si finisce una partita per fare posto.
    case 'capYou':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: toLobby('action.myMatches'),
        cause: null,
      };

    // E' pieno l'ALTRO: non c'e' niente che tu possa fare adesso, e l'invito
    // resta valido. Mandarti in lobby suggerirebbe un rimedio che non esiste.
    case 'capCreator':
      return { titleKey: key('title'), bodyKey: key('body'), action: null, cause: null };

    // Non lo sappiamo: l'unica azione onesta e' rileggere.
    case 'unreachable':
      return {
        titleKey: key('title'),
        bodyKey: key('body'),
        action: { kind: 'retry', labelKey: 'action.retry' },
        cause: null,
      };
  }
}
